import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { auditAPI } from '../../api';

const MONEY_FIELDS = new Set([
  'customer_rate', 'carrier_pay', 'margin', 'fsc_amount', 'customer_line_haul',
  'carrier_line_haul', 'rate_to_cover_min', 'rate_to_cover_max', 'accessorials',
]);

const humanize = (f) => (f || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const fmtVal = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (MONEY_FIELDS.has(field) && !isNaN(parseFloat(v))) {
    return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  }
  return String(v);
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch { return iso; }
};

const ACTION_STYLE = {
  created: { label: 'Created', bg: '#e8f6ee', color: '#1d8a4e' },
  updated: { label: 'Updated', bg: '#e9f0fb', color: '#2a5fc1' },
  deleted: { label: 'Deleted', bg: '#fdeaea', color: '#c62828' },
};

export const UpdatesDialog = ({ open, onOpenChange, entityType, entityId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    auditAPI.getByEntity(entityType, entityId)
      .then((res) => setLogs(res.data || []))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [open, entityType, entityId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="lw-updates-modal">
        <DialogHeader>
          <DialogTitle>Updates</DialogTitle>
          <DialogDescription className="sr-only">Change history for this record</DialogDescription>
        </DialogHeader>
        <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {loading && <p style={{ fontSize: 13, color: '#686868' }}>Loading updates...</p>}
          {!loading && logs.length === 0 && (
            <p style={{ fontSize: 13, color: '#686868' }} data-testid="lw-updates-empty">
              No updates recorded yet. Changes made from now on will appear here.
            </p>
          )}
          {!loading && logs.map((log, i) => {
            const st = ACTION_STYLE[log.action] || ACTION_STYLE.updated;
            return (
              <div key={log.id || i} style={{ border: '1px solid #e3e7ef', borderRadius: 4, padding: '8px 10px' }} data-testid="lw-update-entry">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: log.changes?.length || log.note ? 6 : 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 8px', borderRadius: 10, background: st.bg, color: st.color }}>
                    {st.label}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{log.user_name || 'Unknown'}</span>
                  <span style={{ fontSize: 11, color: '#686868', marginLeft: 'auto' }}>{fmtWhen(log.timestamp)}</span>
                </div>
                {log.note && (
                  <div style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>Reason: {log.note}</div>
                )}
                {(log.changes || []).map((c, j) => (
                  <div key={j} style={{ fontSize: 12, padding: '2px 0', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, minWidth: 110 }}>{humanize(c.field)}</span>
                    <span style={{ color: '#9aa1b0', textDecoration: 'line-through', wordBreak: 'break-word' }}>{fmtVal(c.field, c.old)}</span>
                    <span style={{ color: '#686868' }}>→</span>
                    <span style={{ fontWeight: 600, wordBreak: 'break-word' }}>{fmtVal(c.field, c.new)}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
